/**
 * UpgradeModal Component
 *
 * Modal for upgrading subscription plan.
 * When ENABLE_PAYMENTS=false, upgrades are manual/trial.
 * When ENABLE_PAYMENTS=true, prepares Moyasar checkout via /api/billing/checkout.
 */

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';
import { isPaymentsEnabled } from '../config/plansConfig';
import { saveCheckoutSession } from '../pages/payments/checkoutSession';
import { useNavigate } from 'react-router-dom';
import './UpgradeModal.css';

const UpgradeModal = ({ planId, onClose, onSuccess, billingInterval = 'monthly' }) => {
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCheckoutPrepared = (data) => {
    const paymentId = data.paymentId ? String(data.paymentId) : null;
    if (!paymentId) {
      setError(t('upgrade.checkoutFailed', { defaultValue: 'Failed to prepare checkout.' }));
      return;
    }

    const checkoutPayload = {
      invoiceId: data.invoiceId || null,
      paymentId,
      planId: data.planId || planId,
      billingInterval: data.billingInterval || billingInterval,
      moyasar: data.moyasar || null,
    };

    saveCheckoutSession(paymentId, checkoutPayload);
    onClose();
    navigate(`/payments/checkout/${paymentId}`, { state: { checkout: checkoutPayload } });
  };

  const requestCheckout = async () => {
    const response = await fetch(buildApiUrl(API_ENDPOINTS.BILLING_CHECKOUT), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        planId,
        billingInterval,
      }),
    });

    const data = await response.json();

    if (response.ok && data.ok && data.status === 'prepared') {
      handleCheckoutPrepared(data);
      return;
    }

    setError(
      data.message ||
        data.error ||
        t('upgrade.checkoutFailed', { defaultValue: 'Failed to prepare checkout.' })
    );
  };

  const requestUpgrade = async () => {
    const response = await fetch(buildApiUrl(API_ENDPOINTS.SUBSCRIPTION_UPGRADE), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        userId: user.id,
        planId,
      }),
    });

    const data = await response.json();

    if (
      response.status === 402 ||
      data.error === 'PAYMENT_REQUIRED' ||
      data.checkoutPath === '/api/billing/checkout'
    ) {
      await requestCheckout();
      return;
    }

    if (response.ok && data.success) {
      if (onSuccess) {
        onSuccess(data);
      }
      onClose();
      window.location.reload();
      return;
    }

    setError(data.message || 'Failed to upgrade plan');
  };

  const handleUpgrade = async () => {
    if (!user?.id) {
      navigate('/login', { state: { redirect: '/pricing', upgradePlan: planId } });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (isPaymentsEnabled()) {
        await requestCheckout();
      } else {
        await requestUpgrade();
      }
    } catch (err) {
      console.error('Upgrade error:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const intervalLabel =
    billingInterval === 'annual'
      ? t('pricing.billingInterval.annual')
      : t('pricing.billingInterval.monthly');

  const confirmMessage = isRTL
    ? `هل أنت متأكد من الترقية إلى ${planId} (فوترة ${intervalLabel})؟`
    : `Are you sure you want to upgrade to ${planId} (${intervalLabel} billing)?`;

  const checkoutNote =
    billingInterval === 'annual'
      ? isRTL
        ? 'سيتم تحصيل سعر الخطة السنوية عند الدفع.'
        : 'You will be charged the annual plan price at checkout.'
      : isRTL
        ? 'سيتم تحصيل سعر الخطة الشهرية عند الدفع.'
        : 'You will be charged the monthly plan price at checkout.';

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="upgrade-modal-header">
          <h3>{t('upgrade.title', { defaultValue: 'Upgrade Plan' })}</h3>
          <button className="upgrade-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="upgrade-modal-content">
          <p>{confirmMessage}</p>
          {isPaymentsEnabled() && (
            <p className="upgrade-modal-note">{checkoutNote}</p>
          )}
          {!isPaymentsEnabled() && (
            <div className="upgrade-modal-note">
              <p>{t('upgrade.trialNote', { defaultValue: 'This upgrade will be applied as a trial. No payment required.' })}</p>
            </div>
          )}
          {error && (
            <div className="upgrade-modal-error">
              {error}
            </div>
          )}
        </div>
        <div className="upgrade-modal-actions">
          <button className="upgrade-modal-cancel" onClick={onClose} disabled={isLoading}>
            {t('upgrade.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            className="upgrade-modal-confirm"
            onClick={handleUpgrade}
            disabled={isLoading}
          >
            {isLoading
              ? t('upgrade.processing', { defaultValue: 'Processing...' })
              : isPaymentsEnabled()
                ? t('upgrade.continueToPayment', { defaultValue: 'Continue to Payment' })
                : t('upgrade.confirm', { defaultValue: 'Confirm Upgrade' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
